import * as Yup from 'yup'
import { TaxWithholdingTypes, DistributionReasonInfo, DistributionReasonEnum } from '@Types'
import { map, some, find, values, every, mapValues } from 'lodash'
import { BeneficiaryTypes, LocationOptions } from './Types'
import { CountryItem } from '../../reducers/types'
import { isValidWithholdingType } from '../../components/IraDistributionForm'
import { DistributionReasonTypes } from '../CheckTransfers/Types'
import {
  TransferFrequencyType,
  TransferRecurrenceType,
  WhenToSendUnion
} from '../../components/TransferFrequency/Types'
import { isDateValid } from '../../utils/dateValidation'

const isCountryRestricted = (countryList: CountryItem[], value?: string) => {
  const country = find(countryList, { isoName: value })
  const restrictions = country?.restrictions
  return every(values(restrictions), (item) => !item)
}

// FEECM-16401: The Beneficiary Bank destination dropdown must agree with the entered
// ABA routing number / SWIFT-BIC code, because Sentinel2 charges the wire fee based on
// the SWIFT country code (domestic vs international) regardless of the dropdown. Blocking
// mismatched combinations keeps the fee shown in the UI in sync with what is charged.
export type BankCodeClassification = 'ABA' | 'SWIFT_US' | 'SWIFT_NON_US' | 'UNKNOWN'

const ABA_ROUTING_REGEX = /^\d{9}$/
// SWIFT/BIC: 4-letter bank code, 2-letter ISO country code, 2-char location, optional 3-char branch
const SWIFT_BIC_REGEX = /^[A-Za-z]{4}[A-Za-z]{2}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/

export const classifyBankCode = (value?: string): BankCodeClassification => {
  const code = (value ?? '').trim()
  if (ABA_ROUTING_REGEX.test(code)) {
    return 'ABA'
  }
  if (SWIFT_BIC_REGEX.test(code)) {
    const countryCode = code.slice(4, 6).toUpperCase()
    return countryCode === 'US' ? 'SWIFT_US' : 'SWIFT_NON_US'
  }
  return 'UNKNOWN'
}

export const DOMESTIC_BANK_CODE_MISMATCH_MSG = 'Domestic transfers require an ABA routing number or U.S. SWIFT/BIC code'
export const INTERNATIONAL_BANK_CODE_MISMATCH_MSG = 'International transfers require a non-U.S. SWIFT/BIC code'

export const wireTransferSchema = (
  countryList: CountryItem[],
  defaultValues: {
    amountAvailable: number
    fromAccount: string
    isIra?: boolean
  },
  distributionReasons: DistributionReasonInfo[] | undefined,
  totalDistributionAllowed?: boolean
) =>
  Yup.object({
    totalBalanceCheckbox: Yup.boolean().required().default(false),
    fromAccount: Yup.string().required('Required').default(defaultValues.fromAccount),
    transferAmountDisplay: Yup.string()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.string().required('Required')
      })
      .default(''),
    transferAmountRaw: Yup.number()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.number()
          .nullable()
          .required('Required')
          .typeError('')
          .min(0.01, 'Amount must be greater than $0.00')
          .default(0)
      })
      .default(0),
    beneficiaryType: Yup.mixed<BeneficiaryTypes>().oneOf(map(BeneficiaryTypes)).default(BeneficiaryTypes.AccountHolder),
    beneficiaryAccountNumOrIBAN: Yup.string().required('Required').max(35, 'Maximum 35 characters').default(''),
    beneficiaryName: Yup.string()
      .when('beneficiaryType', {
        is: BeneficiaryTypes.ThirdParty,
        then: Yup.string()
          .required('Required')
          .max(50, 'Maximum 50 characters')
          .matches(/^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    thirdPartyLocation: Yup.mixed().oneOf(map(LocationOptions)).default(LocationOptions.Domestic),
    thirdPartyCountry: Yup.string()
      .when('thirdPartyLocation', {
        is: LocationOptions.International,
        then: Yup.string()
          .required('Required')
          .test('is-invalid', 'Invalid Country', (value) => some(countryList, { isoName: value }))
          .test('is-restricted', 'Blocked Jurisdiction', (value) => {
            return isCountryRestricted(countryList, value)
          })
          .test('is-invalid-intl-location', 'Invalid for international location', (value) => {
            return value !== 'United States'
          })
      })
      .default('United States'),
    thirdPartyCountryCode: Yup.string().default('USA'),
    thirdPartyAddress: Yup.string()
      .when('beneficiaryType', {
        is: BeneficiaryTypes.ThirdParty,
        then: Yup.string().required('Required').max(50, 'Maximum 50 characters')
      })
      .default(''),
    thirdPartyAddressLine2: Yup.string()
      .when('beneficiaryType', {
        is: BeneficiaryTypes.ThirdParty,
        then: Yup.string().max(50, 'Maximum 50 characters')
      })
      .default(''),
    thirdPartyCity: Yup.string().when('beneficiaryType', {
      is: BeneficiaryTypes.ThirdParty,
      then: Yup.string()
        .required('Required')
        .max(100, 'Maximum 100 characters')
        .matches(/^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
    }),
    thirdPartyState: Yup.string()
      .when(['beneficiaryType', 'thirdPartyLocation'], {
        is: (beneficiaryType: string, thirdPartyLocation: string) => {
          return beneficiaryType === BeneficiaryTypes.ThirdParty && thirdPartyLocation === LocationOptions.Domestic
        },
        then: Yup.string().required('Required')
      })
      .default(''),
    thirdPartyZip: Yup.string()
      .when(['beneficiaryType', 'thirdPartyLocation'], {
        is: (beneficiaryType: string, thirdPartyLocation: string) => {
          return beneficiaryType === BeneficiaryTypes.ThirdParty && thirdPartyLocation === LocationOptions.Domestic
        },
        then: Yup.string().required('Required').max(25, 'Maximum 25 characters')
      })
      .default(''),
    bankDestination: Yup.mixed().oneOf(map(LocationOptions)).default(LocationOptions.Domestic),
    bankRouting: Yup.string()
      .required('Required')
      .max(150, 'Maximum 150 characters')
      .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      // FEECM-16401: block bank-code / destination mismatches so the fee shown in the UI
      // (driven by the dropdown) always matches what Sentinel2 charges (driven by the code).
      .test('bank-code-destination-match', DOMESTIC_BANK_CODE_MISMATCH_MSG, function (value) {
        const bankDestination = this.parent.bankDestination
        const classification = classifyBankCode(value)
        if (bankDestination === LocationOptions.Domestic) {
          // Domestic allows an ABA routing number or a U.S. SWIFT; reject non-U.S. SWIFT.
          if (classification === 'SWIFT_NON_US') {
            return this.createError({ message: DOMESTIC_BANK_CODE_MISMATCH_MSG })
          }
        } else if (bankDestination === LocationOptions.International) {
          // International allows only a non-U.S. SWIFT; reject ABA and U.S. SWIFT.
          if (classification === 'ABA' || classification === 'SWIFT_US') {
            return this.createError({ message: INTERNATIONAL_BANK_CODE_MISMATCH_MSG })
          }
        }
        // UNKNOWN / unrecognized codes pass this check (existing charset/length rules apply).
        return true
      })
      .default(''),
    bankName: Yup.string()
      .when('bankDestination', {
        is: LocationOptions.International,
        then: Yup.string()
          .required('Required')
          .max(100, 'Maximum 100 characters')
          .matches(/^$|^[0-9aA-zZ &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    bankCity: Yup.string()
      .when('bankDestination', {
        is: LocationOptions.International,
        then: Yup.string()
          .max(50, 'Maximum 50 characters')
          .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
          .required('Required')
      })
      .default(''),
    bankState: Yup.string()
      .max(50, 'Maximum 50 characters')
      .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      .default(''),
    bankPostalCode: Yup.string()
      .max(25, 'Maximum 25 characters')
      .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      .default(''),
    bankCountry: Yup.string()
      .test('is-invalid', 'Invalid Country', (value) => {
        if (value === '') {
          return true
        }
        return some(countryList, { isoName: value })
      })
      .test('is-restricted', 'Blocked Jurisdiction', (value) => {
        return isCountryRestricted(countryList, value)
      })
      .when('bankDestination', {
        is: LocationOptions.International,
        then: Yup.string()
          .test('is-invalid-intl-location', 'Invalid for international location', (value) => {
            return value !== 'United States'
          })
          .required('Required')
      })
      .default(''),
    bankUseIntermediary: Yup.boolean().default(false),
    termsOfUse: Yup.boolean().required().oneOf([true], 'The terms and conditions must be accepted.').default(false),
    intermediaryName: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string()
          .required('Required')
          .max(50, 'Maximum 50 characters')
          .matches(/^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    intermediaryAccountNumber: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string().required('Required').max(25, 'Maximum 25 characters')
      })
      .default(''),
    intermediaryAddress: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string().required('Required').max(100, 'Maximum 100 characters')
      })
      .default(''),
    intermediaryAddressLine2: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string().max(100, 'Maximum 100 characters')
      })
      .default(''),
    intermediaryCity: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string()
          .required('Required')
          .max(50, 'Maximum 50 characters')
          .matches(/^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    intermediaryState: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string()
          .max(50, 'Maximum 50 characters')
          .matches(/^$|[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    intermediaryZip: Yup.string()
      .when('bankUseIntermediary', {
        is: true,
        then: Yup.string()
          .max(25, 'Maximum 25 characters')
          .matches(/^$|[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    intermediaryCountry: Yup.string().when('bankUseIntermediary', {
      is: true,
      then: Yup.string()
        .required('Required')
        .test('is-invalid', 'Invalid Country', (value) => some(countryList, { isoName: value }))
        .test('is-restricted', 'Blocked Jurisdiction', (value) => {
          return isCountryRestricted(countryList, value)
        })
        .default('')
    }),
    distributionReason: Yup.lazy(() => {
      if (defaultValues.isIra) {
        return Yup.mixed<DistributionReasonEnum | ''>()
          .test('distribution-reason', 'Required', (value) => {
            if (value) {
              return map(DistributionReasonEnum).includes(value)
            }
            return false
          })
          .default('')
      }
      return Yup.mixed<DistributionReasonTypes>().default('')
    }),
    federalTaxWithholdingType: Yup.mixed<TaxWithholdingTypes>().default(null),
    federalTaxWithholdingAmountRaw: Yup.number()
      .when('federalTaxWithholdingType', {
        is: (type: string) => type && type?.length > 0,
        then: Yup.number()
          .when('federalTaxWithholdingType', {
            is: (federalTaxWithholdingType: TaxWithholdingTypes) => federalTaxWithholdingType === 'FIXED',
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than $0.00')
          })
          .when('federalTaxWithholdingType', {
            is: (federalTaxWithholdingType: TaxWithholdingTypes) => federalTaxWithholdingType === 'PERCENTAGE',
            then: Yup.number()
              .nullable()
              .required('Required')
              .typeError('')
              .min(0.01, 'Amount must be greater than 0%')
              .max(100, 'Amount must be lower than 100%')
          })
      })
      .default(0),
    federalTaxWithholdingAmountDisplay: Yup.string()
      .when('distributionReason', {
        is: (value: string) => distributionReasons?.some((item) => item.reason === value) && defaultValues.isIra,
        then: Yup.string()
          .when('federalTaxWithholdingType', {
            is: (federalTaxWithholdingType: TaxWithholdingTypes) => isValidWithholdingType(federalTaxWithholdingType),
            then: Yup.string().required('Required')
          })
          .default('')
      })
      .default(''),
    stateTaxWithholdingAmountDisplay: Yup.string()
      .when('distributionReason', {
        is: (value: string) => distributionReasons?.some((item) => item.reason === value),
        then: Yup.string()
          .when('stateTaxWithholdingType', {
            is: (stateTaxWithholdingType: TaxWithholdingTypes) =>
              isValidWithholdingType(stateTaxWithholdingType) && defaultValues.isIra,
            then: Yup.string().required('Required')
          })
          .default('')
      })
      .default(''),
    stateTaxWithholdingAmountRaw: Yup.number()
      .when('stateTaxWithholdingType', {
        is: (type: string) => type && type?.length > 0,
        then: Yup.number()
          .when('stateTaxWithholdingType', {
            is: (stateTaxWithholdingType: TaxWithholdingTypes) => stateTaxWithholdingType === 'FIXED',
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than $0.00')
          })
          .when('stateTaxWithholdingType', {
            is: (stateTaxWithholdingType: TaxWithholdingTypes) => stateTaxWithholdingType === 'PERCENTAGE',
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than 0%')
              .max(100, 'Amount must be lower than 100%')
          })
      })
      .default(0),
    stateTaxWithholdingType: Yup.mixed<TaxWithholdingTypes>().default(null),
    receivingInstitutionName: Yup.string()
      .when('distributionReason', {
        is: (value: string) => value === 'TRANSFER',
        then: Yup.string().required('Required')
      })
      .default(''),
    transferRecurrence: Yup.mixed<TransferRecurrenceType>().default('ONE_TIME'),
    whenToSend: Yup.mixed<WhenToSendUnion>().default('IMMEDIATELY'),
    transferScheduledDate: Yup.string()
      .when('whenToSend', {
        is: 'IN_THE_FUTURE',
        then: Yup.string()
          .test({
            message: () => '',
            test: function (value, context) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    transferFrequency: Yup.mixed<TransferFrequencyType>()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.mixed<TransferFrequencyType>().required('Required').default('WEEKLY')
      })
      .default('WEEKLY'),
    transferStartDate: Yup.string()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.string()
          .test({
            message: () => 'Invalid date',
            test: function (value) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .test({
            message: () => 'Recurring transfers not allowed for total balance transfers',
            test: function (value, context) {
              return !context.parent.totalBalanceCheckbox
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    numberOfTransfers: Yup.number()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.number().max(100, 'Maximum 100').min(1, 'Value must be greater than 0').default(undefined)
      })
      .default(undefined),
    hasTotalDistributionError: Yup.boolean().when('totalBalanceCheckbox', {
      is: true,
      then: Yup.boolean().test({
        message: () => 'This type of IRA account does not support a total amount transfer.',
        test: () => {
          if (!!defaultValues.isIra) {
            return !!totalDistributionAllowed
          }
          return true
        }
      })
    }),
    fees: Yup.lazy((obj) =>
      Yup.object(
        mapValues(obj, () =>
          Yup.object({
            chargeCustomer: Yup.boolean().default(false),
            feeTotal: Yup.number().default(0)
          })
        )
      ).default({})
    )
  })
